/**
 * OaSIS Explorer Frontend - Debounce Hook
 * Task 4.1: Frontend Code Optimization - Consolidation Phase
 * 
 * Shared debounce logic to eliminate duplicate code
 */

import React from 'react';

/**
 * Custom hook for debouncing values
 * Consolidates duplicate debouncing logic across components
 */
export const useDebounce = <T>(value: T, delay: number): T => {
  const [debouncedValue, setDebouncedValue] = React.useState<T>(value);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
};

/**
 * Custom hook for debounced search with auto-mode switching
 * Consolidates search patterns used in multiple components
 */
export const useDebouncedSearch = (
  initialValue: string = '',
  delay: number = 300
) => {
  const [searchQuery, setSearchQuery] = React.useState(initialValue);
  const debouncedQuery = useDebounce(searchQuery, delay);

  const clearSearch = React.useCallback(() => {
    setSearchQuery('');
  }, []);

  return {
    searchQuery,
    setSearchQuery,
    debouncedQuery,
    clearSearch,
    hasQuery: debouncedQuery.length >= 2,
  };
};
